<template>
  <div class="form__group form__group--move dropdown"
    :class="[domclass, getClasses]"
    ref="LennocFilteredSearchSelect">
    <div class="dropdown__content" v-if="isOpen" :class="showComponent">
      <ul class="dropdown__list">
        <li
          class="dropdown__item"
          v-for="(item, index) in items"
          :key="item.Code + index"
          :class="{ 'dropdown__item--selected' : isSelected(index) }"
          @click="setResult(item)">
          {{ item.Name }}<span v-if="code"> ({{ item.Code }})</span>
        </li>
      </ul>
    </div>
    <div
      class="form__input"
      :class="isFocused"
      @keydown.down="onArrowDown"
      @keydown.up="onArrowUp"
      @keydown.enter="onEnter"
      @keydown.tab.prevent="onTab">
      <span v-if="!disabled" class="hitzone" @click="onChange" @blur="onBlur">
        <LennocIcon image="magnify" />
      </span>
      <slot></slot>
      <input
        v-bind:value="search"
        :id="id"
        type="text"
        :disabled="disabled"
        autocomplete="off"
        @input="onChange"
        @focus="onFocus"
        @blur="onBlur"
      />
    </div>
    <label v-if="label" :for="id">{{ label }}</label>
    <div v-if="internalError" class="error">{{ validationText }}</div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, PropType, onMounted, watch, onUnmounted, nextTick, computed } from 'vue'
  import { ApiListItem, ValidatorObject, GetListByInputPayload } from '../../types/types'

  const props = defineProps({
    id: {
      type: String,
      required: true,
      default: ''
    },
    listToFilter: {
      type: String,
      required: true
    },
    characterSearch: {
      type: Number,
      default: 2
    },
    items: {
      type: Array as PropType<Array<ApiListItem>>,
      default: []
    },
    modelValue: {
      type: Object as PropType<ApiListItem>,
      default: {}
    },
    validators: {
      type: Array as PropType<Array<ValidatorObject>>,
      default: [],
      validator (value : Array<ValidatorObject>) {
        value.forEach(vld => {
          if (
            !Object.hasOwnProperty.call(vld, 'validate') ||
            typeof vld.validate !== 'function' ||
            !Object.hasOwnProperty.call(vld, 'message') ||
            typeof vld.message !== 'string'
          ) {
            return false
          }
        })
        return true
      }
    },
    label: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    domclass: {
      type: String,
      default: ''
    },
    code: {
      type: Boolean,
      default: false
    },
    validationText: {
      type: String,
      default: ''
    }
  })

  const emits = defineEmits([
    'blur',
    'clear',
    'focus',
    'getListFromApi',
    'inputChange',
    'setHeight'
  ])

  const isFocused = computed(() => {
    return hasFocus.value || internalError.value
      ? 'form__input--focus'
      : ''
  })

  const showComponent = computed(() => {
    return isOpen.value && 
      !props.disabled && 
      (search.value && search.value.length > props.characterSearch) 
        ? 'dropdown__content--visible'
        : ''
  })

  const getClasses = computed(() => {
    return [
      internalError.value ? 'form__group--error' : '',
      Object.keys(props.modelValue).length ? 'form__group--touched' : '']
    .join(' ')
  })

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    Object.keys(props.modelValue).length
      ? emits('getListFromApi', {
        filter: props.modelValue.Name,
        list: props.listToFilter,
        url: 'filterlist'
      } as GetListByInputPayload)
      : undefined
  }) 

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })

  watch(props, () => {
    const modelValueNotPresent = () => {
      search.value = !hasFocus.value && !internalError.value
        ? ''
        : search.value
    }
    const modelValuePresent = () => {
      internalError.value = false
      search.value = !hasFocus.value
        ? props.modelValue.Name
        : search.value
    }
    !Object.keys(props.modelValue).length
      ? modelValueNotPresent()
      : modelValuePresent()
    emits('setHeight')
  })

  const internalError = ref<boolean>(false)
  const isOpen = ref<boolean>(false)
  const hasFocus = ref<boolean>(false)
  const selectedIndex = ref<number>(-1)
  const search = ref<string>('')
  const LennocFilteredSearchSelect = ref()

  const getListCondition = (event : Event) => {
    return !!(event.type === 'input' &&
      search.value &&
      search.value.length > props.characterSearch)
  }

  const setResult = (item : ApiListItem | Record<string, any>) => {
    internalError.value = false
    if (!Object.keys(item).length) {
      emits('clear')
      return
    }
    search.value = item.Name
    isOpen.value = false
    emits('inputChange', item)
  }

  const isSelected = (index : number) => {
    return index === selectedIndex.value
  }

  const onChange = (event : Event) => {
    const targetEl = event.target as HTMLInputElement
    search.value = targetEl.value
    internalError.value = false
    hasFocus.value = true
    if (!targetEl.value.length) {
      setResult({})
    }
    if (event.type === 'input') {
      emits('inputChange')
    }
    if (getListCondition(event)) {
      emits('getListFromApi', {
        url: 'filterlist',
        filter: search.value,
        list: props.listToFilter
      })
      isOpen.value = true
    }
  }

  const onBlur = async () => {
    hasFocus.value = false
    await nextTick
    if ((search.value && search.value.length >= props.characterSearch) && Object.keys(props.modelValue).length === 0) {
      internalError.value = true
      emits('setHeight')
    }
    emits('blur')
  }

  const onFocus = () => {
    hasFocus.value = true
    internalError.value = false
    emits('focus')
    isOpen.value = true
  }

  const onArrowDown = () => {
    selectedIndex.value = Math.min(
      selectedIndex.value + 1,
      props.items.length - 1
    )
  }

  const onArrowUp = () => {
    selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
  }

  const onEnter = () => {
    setResult(props.items[selectedIndex.value])
  }

  const onTab = () => {
    if (isOpen.value) {
      setResult(props.items[selectedIndex.value])
    }
  }
  
  const handleClickOutside = (event : Event) : void => {
    if (event && !LennocFilteredSearchSelect.value.contains(event.target)) {
      isOpen.value = false
    }
  }

</script>